import { Button, Container, Form, FormText, InputGroup, ProgressBar } from "react-bootstrap";
import { useState } from "react";
import { api_url, api_token } from "../../context";
import axios from "axios";
import { formatDateYMD, parseStrictDateYMD } from "../../utils/date";
import PageHeader from "../molecules/PageHeader";

const CSV_HEADER_KEYS = [
    'first_name',
    'last_name',
    'address1',
    'address2',
    'city',
    'state',
    'zip',
    'home_phone',
    'mobile_phone',
    'work_phone',
    'primary_email',
    'secondary_email',
    'organization',
    'dnc_request_date'
]

// How many add_dnc requests we allow in flight at once. Sending hundreds of
// requests at the same time overwhelms the browser's per-host connection
// limit and the API, which is what was causing large imports to hang forever.
const MAX_CONCURRENT_REQUESTS = 5;

const normalizeName = (v) => (v || '').toString().trim().toLowerCase();
const normalizePhone = (v) => (v || '').toString().replace(/\D/g, '');
const normalizeEmail = (v) => (v || '').toString().trim().toLowerCase();

// Normalizes the "Request Date" column to YYYY-MM-DD (matching what
// AddDnc.js sends), or returns null if the value can't be confidently
// parsed, so the caller can fail that row deterministically instead of
// sending something the API might reject or store inconsistently.
const normalizeDncDate = (value) => {
    const trimmed = (value || '').toString().trim();
    if (!trimmed) return formatDateYMD(new Date()); // default to today, same as AddDnc's "new Date()" default
    return parseStrictDateYMD(trimmed);
}

// Coerces whatever the API/error object handed us into a readable string.
// insert_result/message are expected to be strings, but if the API ever
// returns a structured object/array there instead, interpolating it directly
// would render as "[object Object]" - stringify it so it's still legible.
const toDisplayString = (value) => {
    if (typeof value === 'string') return value;
    try {
        return JSON.stringify(value);
    } catch {
        return String(value);
    }
}

// Pulls a human-readable reason out of a failed add_dnc request. The API
// returns its own error detail in response.data.insert_result (same field
// AddDnc.js reads for single-contact errors) - fall back to the HTTP status
// or a network-level message if that's not present.
const getApiErrorMessage = (err) => {
    if (err?.response?.data?.insert_result) return toDisplayString(err.response.data.insert_result);
    if (err?.response?.data?.message) return toDisplayString(err.response.data.message);
    if (err?.response?.status) return `Server error (status ${err.response.status})`;
    if (err?.message) return err.message;
    return 'Unknown error';
}

const fullName = (row) => {
    const fn = row.first_name ? row.first_name.trim() : '';
    const ln = row.last_name ? row.last_name.trim() : '';
    return (fn + ' ' + ln).trim() || '(no name)';
}

// A row counts as a duplicate if the first + last name match an existing
// record AND at least one phone number or email also matches. Matching on
// name alone would risk skipping two different people who share a name.
const isDuplicate = (row, existingRecords) => {
    const fn = normalizeName(row.first_name);
    const ln = normalizeName(row.last_name);
    // Require both first AND last name to be present before attempting to
    // match. Matching on just one (e.g. only "John") risks flagging
    // unrelated people who share a common first or last name as duplicates.
    if (!fn || !ln) return false;

    const rowPhones = [row.home_phone, row.mobile_phone, row.work_phone].map(normalizePhone).filter(Boolean);
    const rowEmails = [row.primary_email, row.secondary_email].map(normalizeEmail).filter(Boolean);

    return existingRecords.some((ex) => {
        if (normalizeName(ex.first_name) !== fn || normalizeName(ex.last_name) !== ln) return false;
        const exPhones = [ex.home_phone, ex.mobile_phone, ex.work_phone].map(normalizePhone).filter(Boolean);
        const exEmails = [ex.primary_email, ex.secondary_email].map(normalizeEmail).filter(Boolean);
        const phoneMatch = rowPhones.length && exPhones.length && rowPhones.some((p) => exPhones.includes(p));
        const emailMatch = rowEmails.length && exEmails.length && rowEmails.some((e) => exEmails.includes(e));
        return phoneMatch || emailMatch;
    });
}

// Runs `worker` over `items` with at most `concurrency` requests in flight
// at a time, instead of firing everything at once. Each item's result is
// caught individually so one failed/slow request can't block the rest.
const runWithConcurrency = async (items, worker, concurrency, onSettle) => {
    const results = new Array(items.length);
    let cursor = 0;

    const runNext = async () => {
        while (cursor < items.length) {
            const current = cursor++;
            try {
                const value = await worker(items[current]);
                results[current] = { status: 'fulfilled', value };
            } catch (reason) {
                results[current] = { status: 'rejected', reason };
            }
            if (onSettle) onSettle();
        }
    };

    const workers = Array(Math.min(concurrency, items.length)).fill(0).map(runNext);
    await Promise.all(workers);
    return results;
}

// Tokenizes a full CSV string into rows of raw field values, respecting
// double-quoted fields. This means commas and newlines *inside* quotes
// (e.g. an address like "123 Main St, Apt 2") don't split into extra
// columns, and "" inside a quoted field is unescaped to a literal ".
// A naive line.split(',') would misalign every column after a quoted comma.
const tokenizeCsv = (str) => {
    const rows = [];
    let row = [];
    let field = '';
    let inQuotes = false;

    for (let i = 0; i < str.length; i++) {
        const char = str[i];

        if (inQuotes) {
            if (char === '"') {
                if (str[i + 1] === '"') {
                    field += '"';
                    i++;
                } else {
                    inQuotes = false;
                }
            } else {
                field += char;
            }
            continue;
        }

        if (char === '"') {
            inQuotes = true;
        } else if (char === ',') {
            row.push(field);
            field = '';
        } else if (char === '\r') {
            // ignore, \n (below) ends the row
        } else if (char === '\n') {
            row.push(field);
            rows.push(row);
            row = [];
            field = '';
        } else {
            field += char;
        }
    }

    if (field !== '' || row.length > 0) {
        row.push(field);
        rows.push(row);
    }

    return rows;
}

const parseCsv = (str) => {
    const rows = tokenizeCsv(str);
    const dataRows = rows.slice(1).filter((values) => values.some((v) => v.trim() !== ''));

    return dataRows.map((values) => {
        const row = {};
        CSV_HEADER_KEYS.forEach((key, i) => {
            row[key] = values[i] !== undefined ? values[i].trim() : '';
        });
        return row;
    });
}

export default function Import() {
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(false);
    const [file, setFile] = useState(null);
    const [progress, setProgress] = useState({ done: 0, total: 0 });
    const [summary, setSummary] = useState(null);

    const sendRequest = (data) => {
        const headers = {
            'Content-Type': 'application/json',
            'Authorization': 'Bearer ' + api_token,
        }
        return axios.post(
            api_url + '/add_dnc',
            data,
            { headers: headers }
        );
    }

    // NOTE: this downloads every existing DNC record to do dedupe matching in
    // the browser. That's a real scaling/PII-exposure concern as the list
    // grows, but it's the only option available today - the API (see
    // src/api_details.md) only exposes add_dnc and get_dnc; there's no
    // server-side lookup/exists-check endpoint to check a name against
    // without pulling the full dataset. Fixing this properly requires a new
    // backend endpoint (e.g. a dedupe-key lookup); tracked as a follow-up,
    // not something fixable from this frontend-only change.
    const fetchExistingRecords = () => {
        return axios.get(api_url + '/get_dnc', {
            headers: {
                'Content-Type': 'application/json',
                'Authorization': 'Bearer ' + api_token,
            }
        })
        .then((res) => Object.values(res.data || {}))
        .catch(() => []); // if this fails we still proceed, just without dedupe protection
    }

    const handleSubmit = (e) => {
        e.preventDefault();
        if (!file) return;

        setLoading(true);
        setError(false);
        setSummary(null);
        setProgress({ done: 0, total: 0 });

        const reader = new FileReader();
        reader.onerror = () => {
            setLoading(false);
            setError(true);
        }
        reader.onload = async (e) => {
            const rows = parseCsv(e.target.result);

            if (rows.length === 0) {
                setLoading(false);
                setError(true);
                return;
            }

            const existingRecords = await fetchExistingRecords();

            // Track names we've already decided to submit so duplicates
            // *within the same file* are caught too, not just ones already
            // in the database.
            const seen = [...existingRecords];
            const toSubmit = [];
            const skipped = [];
            const invalidDates = [];

            rows.forEach((row) => {
                if (isDuplicate(row, seen)) {
                    skipped.push(fullName(row));
                    return;
                }
                seen.push(row);

                // Normalize the date to YYYY-MM-DD (matching what AddDnc.js
                // sends) before it's ever posted. If it can't be parsed
                // confidently, fail this row now with a clear reason instead
                // of sending something the API might reject or store
                // inconsistently.
                const normalizedDate = normalizeDncDate(row.dnc_request_date);
                if (normalizedDate === null) {
                    const enteredValue = (row.dnc_request_date || '').trim() || '(blank)';
                    invalidDates.push(`${fullName(row)} — entered ${JSON.stringify(enteredValue)}, expected a date like YYYY-MM-DD`);
                    return;
                }

                toSubmit.push({ ...row, dnc_request_date: normalizedDate });
            });

            setProgress({ done: 0, total: toSubmit.length });

            const results = await runWithConcurrency(
                toSubmit,
                sendRequest,
                MAX_CONCURRENT_REQUESTS,
                () => setProgress((p) => ({ ...p, done: p.done + 1 }))
            );

            const failed = [];
            let addedCount = 0;
            results.forEach((r, i) => {
                if (r.status === 'fulfilled') {
                    addedCount++;
                } else {
                    failed.push(`${fullName(toSubmit[i])} — ${getApiErrorMessage(r.reason)}`);
                }
            });

            setSummary({
                total: rows.length,
                added: addedCount,
                skipped,
                invalidDates,
                failed
            });
            setLoading(false);
            document.getElementById('form')?.reset();
            setFile(null);
        }
        reader.readAsText(file);
    }

    // Renders a count + message, followed by the full list of names (one per
    // line, in a scrollable box so a long list - e.g. dozens of duplicates
    // or failures - doesn't turn into one unreadable wall of text, but is
    // still fully visible rather than being cut off with "and X more").
    const renderNameSection = (className, message, names) => (
        <div className={className + ' mb-2'}>
            <p className="mb-1">{message}</p>
            <ul style={{ maxHeight: 200, overflowY: 'auto', marginBottom: 0 }}>
                {names.map((name, i) => <li key={i}>{name}</li>)}
            </ul>
        </div>
    )

    return (
        <Container>
            <PageHeader title="Import" />

            {!loading ?
            <Form
                id="form"
                onSubmit={handleSubmit}>
                <InputGroup>
                    <Form.Control
                        type="file"
                        id="file"
                        bsCustomPrefix="custom-file-input"
                        className="h-100"
                        accept=".csv"
                        onChange={(e) => {
                            setFile(e.target.files[0])
                        }}
                    >
                    </Form.Control>
                    <Button variant="primary" type="Submit">Submit</Button>
                </InputGroup>

                <FormText style={{textAlign: 'left'}}>
                    Please upload a csv file.
                    <p>Note: must be in this format: <br />
                    <code>
                        First Name, Last Name, Address1, Address 2, City, State, Zip, Home Phone, Mobile Phone, Work Phone, Email Address, Secondary Email Address, Organization, Request Date (YYYY-MM-DD)
                    </code>
                    </p>
                    <p>Do not include a header row that differs from this order &mdash; columns are matched by position. If a field (like an address) contains a comma, wrap it in double quotes, e.g. <code>"123 Main St, Apt 2"</code>.</p>
                 </FormText>

            </Form>  : null }

            {loading &&
                <>
                    <p className="text-info">
                        {progress.total > 0
                            ? `Importing, please wait... (${progress.done} of ${progress.total})`
                            : 'Checking for duplicates, please wait...'}
                    </p>
                    {progress.total > 0 &&
                        <ProgressBar now={(progress.done / progress.total) * 100} />
                    }
                </>
            }
            {error ? <p className="text-danger">We couldn't read any rows from that file. Please check the format and try again.</p> : null}
            {summary &&
                <div className="text-start">
                    <p className="text-success">Import complete. <a href="/">return to contacts</a></p>
                    <p>{summary.added} of {summary.total} contact(s) added.</p>
                    {summary.skipped.length > 0 && renderNameSection(
                        'text-warning',
                        `${summary.skipped.length} skipped as likely duplicate(s) already on the list:`,
                        summary.skipped
                    )}
                    {summary.invalidDates.length > 0 && renderNameSection(
                        'text-danger',
                        `${summary.invalidDates.length} not imported due to an unrecognized Request Date value (expected YYYY-MM-DD), please fix and re-upload these individually:`,
                        summary.invalidDates
                    )}
                    {summary.failed.length > 0 && renderNameSection(
                        'text-danger',
                        `${summary.failed.length} failed to import, please retry these individually:`,
                        summary.failed
                    )}
                </div>
            }
        </Container>
    )
}
